import type { AnalyticsListener } from '../types/listener.js';
import type { EventEnvelope } from '../types/envelope.js';

export interface GAListenerConfig {
  measurementId: string;
}

const GTAG_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function loadGtagScript(measurementId: string): Promise<void> {
  return new Promise((resolve) => {
    if (!isBrowser()) {
      resolve();
      return;
    }
    if (window.gtag) {
      window.gtag('config', measurementId);
      resolve();
      return;
    }
    window.dataLayer = window.dataLayer ?? [];
    const gtag = (...args: unknown[]) => {
      window.dataLayer!.push(args);
    };
    window.gtag = gtag;
    gtag('js', new Date());
    const script = document.createElement('script');
    script.async = true;
    script.src = `${GTAG_SCRIPT_URL}?id=${encodeURIComponent(measurementId)}`;
    script.onload = () => {
      gtag('config', measurementId);
      resolve();
    };
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

/**
 * Listener that sends events to Google Analytics 4 via gtag.
 * Loads gtag script with measurementId; track → gtag('event', name, properties).
 * No-op when typeof window === 'undefined' (SSR).
 */
export function createGAListener(config: GAListenerConfig): AnalyticsListener {
  const measurementId = config.measurementId;
  let ready = false;

  return {
    async init(cfg: unknown): Promise<void> {
      try {
        const c = (cfg ?? config) as GAListenerConfig;
        const id = c.measurementId;
        if (!id || !isBrowser()) return;
        await loadGtagScript(id);
        ready = true;
      } catch {
        // swallow
      }
    },
    track(envelope: EventEnvelope): void {
      try {
        if (!isBrowser() || !ready || typeof window.gtag !== 'function') return;
        const params = envelope.properties ?? {};
        if (envelope.userId) (params as Record<string, unknown>)['user_id'] = envelope.userId;
        window.gtag('event', envelope.name, params);
      } catch {
        // swallow
      }
    },
    identify(userId: string, traits?: Record<string, unknown>): void {
      try {
        if (!isBrowser() || typeof window.gtag !== 'function') return;
        window.gtag('set', 'user_properties', { user_id: userId, ...traits });
      } catch {
        // swallow
      }
    },
  };
}
